import { useCallback, useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { createFileRoute } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAdminContext } from "@/lib/admin-context";
import { showToast } from "@/lib/ui/toast";

/**
 * Admin → Plan waitlist.
 *
 * Port of `src/app/admin/plan-waitlist/plan-waitlist-client.tsx` (legacy NextJS, ~330 LOC) per D21.
 *
 * Endpoints:
 *   - GET  /api/admin/plan-waitlist                       → plans list (with totals)
 *   - GET  /api/admin/plan-waitlist?planCode=XX          → entries
 *   - POST /api/admin/plan-waitlist/notify { planCode }  → manual notify
 *   - GET  /api/admin/plan-waitlist/export?planCode=XX   → CSV
 *
 * The CSV export is streamed through `apiFetch` rather than linked with an
 * `<a href>`: the admin API lives on api.pontimed.app (a relative path would
 * download the SPA shell from admin.pontimed.app) and the export is scoped by
 * the `X-Admin-DB` header that the admin fetch interceptor injects, which a
 * plain anchor navigation cannot carry.
 */

interface PlanRow {
  code: string;
  name: Record<string, string>;
  targetType: string;
  visibility: string;
  isActive: boolean;
  total: number;
  pending: number;
}

interface Entry {
  id: string;
  planCode: string;
  countryCode: string | null;
  name: string;
  email: string;
  locale: string;
  createdAt: string;
  notifiedAt: string | null;
}

/** Sentinel for the "all plans" export — plan codes are never empty. */
const ALL_PLANS = "__all__";

const TARGET_LABEL: Record<string, string> = {
  provider: "Provider",
  facility: "Facility",
  business: "Business",
  user: "User",
};

export const Route = createFileRoute("/_authenticated/plan-waitlist")({
  component: PlanWaitlistPage,
});

function PlanWaitlistPage() {
  const { t } = useLingui();
  const { dbTarget } = useAdminContext();
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState<string | null>(null);
  const [notifyResult, setNotifyResult] = useState<string>("");
  const [exportingCode, setExportingCode] = useState<string | null>(null);

  const fetchPlans = useCallback(() => {
    setLoading(true);
    apiFetch
      .get<{ plans: PlanRow[] }>("/api/admin/plan-waitlist")
      .then((data) => setPlans(data.plans || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    // dbTarget refresh handled by parent useEffect dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbTarget]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    if (!selectedPlan) {
      setEntries([]);
      return;
    }
    apiFetch
      .get<{ entries: Entry[] }>(
        `/api/admin/plan-waitlist?planCode=${encodeURIComponent(selectedPlan)}`,
      )
      .then((data) => setEntries(data.entries || []))
      .catch(() => {});
  }, [selectedPlan, dbTarget]);

  const handleNotify = async (planCode: string) => {
    if (!confirm(t`Send activation email to all in waitlist for "${planCode}"?`)) return;
    setNotifying(planCode);
    setNotifyResult("");
    try {
      const data = await apiFetch.post<{ sent: number; skipped: number; total: number }>(
        "/api/admin/plan-waitlist/notify",
        { planCode },
      );
      setNotifyResult(
        `✅ ${planCode}: ${data.sent} sent, ${data.skipped} skipped (total ${data.total})`,
      );
      fetchPlans();
      if (selectedPlan === planCode) {
        const e = await apiFetch
          .get<{
            entries: Entry[];
          }>(`/api/admin/plan-waitlist?planCode=${encodeURIComponent(planCode)}`)
          .catch(() => ({ entries: [] }));
        setEntries(e.entries || []);
      }
    } catch {
      setNotifyResult(`❌ ${planCode}: error`);
    } finally {
      setNotifying(null);
    }
  };

  const totalPending = plans.reduce((s, p) => s + p.pending, 0);
  const totalAll = plans.reduce((s, p) => s + p.total, 0);
  const comingSoonPlans = plans.filter((p) => p.visibility === "coming_soon");
  const plansWithWaitlist = plans.filter((p) => p.total > 0);

  const exportCsv = async (code?: string) => {
    const path = code
      ? `/api/admin/plan-waitlist/export?planCode=${encodeURIComponent(code)}`
      : `/api/admin/plan-waitlist/export`;
    setExportingCode(code ?? ALL_PLANS);
    const toast = showToast(t`Preparing CSV…`, "loading");
    try {
      const res = await apiFetch.raw(path, { method: "GET" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `plan-waitlist-${code ?? "all"}-${new Date().toISOString().slice(0, 10)}.csv`;
      // Attach before clicking: a detached anchor is ignored by some engines.
      // Revoke on the next tick — revoking synchronously can cancel a download
      // the browser has only just started.
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      toast.update(t`CSV downloaded`, "success");
    } catch {
      toast.update(t`CSV export failed`, "error");
    } finally {
      setExportingCode(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          <Trans>Plan waitlist</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>
            Track activation signups per "Coming soon" plan. Notification fires automatically when
            you flip the plan from coming_soon to active in Plans &amp; Pricing, or manually here.
          </Trans>
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: t`Plans coming_soon`, value: comingSoonPlans.length, icon: "⏳" },
          { label: t`Total signups`, value: totalAll, icon: "📋" },
          { label: t`Pending notification`, value: totalPending, icon: "🔔" },
          { label: t`Plans with signups`, value: plansWithWaitlist.length, icon: "📊" },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <span className="text-2xl">{s.icon}</span>
              <div className="text-primary mt-2 text-2xl font-bold">{s.value}</div>
              <div className="text-muted-foreground text-xs">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {notifyResult && (
        <div className="border-border/50 bg-muted/30 rounded-lg border p-3 text-sm">
          {notifyResult}
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            <Trans>Plans</Trans>
          </CardTitle>
          <button
            type="button"
            disabled={exportingCode !== null}
            onClick={() => void exportCsv()}
            className="text-primary text-xs underline disabled:opacity-60"
          >
            {exportingCode === ALL_PLANS ? (
              <Trans>Exporting…</Trans>
            ) : (
              <Trans>Export CSV (all)</Trans>
            )}
          </button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-muted-foreground p-4 text-center text-sm">
              <Trans>Loading...</Trans>
            </div>
          ) : plansWithWaitlist.length === 0 && comingSoonPlans.length === 0 ? (
            <div className="border-border/50 rounded-lg border border-dashed p-8 text-center">
              <span className="text-4xl">📋</span>
              <p className="text-muted-foreground mt-3 text-sm">
                <Trans>
                  No plans with waitlist. Mark a plan as "coming_soon" in Plans &amp; Pricing.
                </Trans>
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-border/50 border-b text-left">
                    <th className="px-3 py-2 font-medium">
                      <Trans>Plan</Trans>
                    </th>
                    <th className="px-3 py-2 font-medium">
                      <Trans>Type</Trans>
                    </th>
                    <th className="px-3 py-2 font-medium">
                      <Trans>Status</Trans>
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      <Trans>Total</Trans>
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      <Trans>Pending</Trans>
                    </th>
                    <th className="px-3 py-2 text-right font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {plans
                    .filter((p) => p.total > 0 || p.visibility === "coming_soon")
                    .map((p) => {
                      const isSelected = selectedPlan === p.code;
                      return (
                        <tr
                          key={p.code}
                          className={`border-border/30 border-b transition-colors ${
                            isSelected ? "bg-primary/5" : "hover:bg-muted/30"
                          }`}
                        >
                          <td className="px-3 py-2.5">
                            <button
                              onClick={() => setSelectedPlan(isSelected ? "" : p.code)}
                              className="text-left"
                            >
                              <div className="font-semibold">{p.name.ro || p.name.en}</div>
                              <div className="text-muted-foreground text-[0.625rem]">{p.code}</div>
                            </button>
                          </td>
                          <td className="px-3 py-2.5">
                            <Badge variant="secondary" className="text-[0.625rem]">
                              {TARGET_LABEL[p.targetType] || p.targetType}
                            </Badge>
                          </td>
                          <td className="px-3 py-2.5">
                            {p.visibility === "coming_soon" && (
                              <Badge className="bg-amber-100 text-[0.625rem] text-amber-900 hover:bg-amber-100">
                                <Trans>Coming soon</Trans>
                              </Badge>
                            )}
                            {p.visibility === "active" && (
                              <Badge variant="outline" className="text-[0.625rem] text-emerald-600">
                                <Trans>Active</Trans>
                              </Badge>
                            )}
                            {p.visibility === "draft" && (
                              <Badge variant="secondary" className="text-[0.625rem]">
                                <Trans>Draft</Trans>
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right font-semibold">{p.total}</td>
                          <td className="px-3 py-2.5 text-right">
                            {p.pending > 0 ? (
                              <span className="font-semibold text-amber-600">{p.pending}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center justify-end gap-2">
                              {p.total > 0 && (
                                <button
                                  type="button"
                                  disabled={exportingCode !== null}
                                  onClick={() => void exportCsv(p.code)}
                                  className="text-muted-foreground hover:text-primary text-[0.6875rem] underline disabled:opacity-60"
                                >
                                  {exportingCode === p.code ? "…" : "CSV"}
                                </button>
                              )}
                              {p.pending > 0 && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  disabled={notifying === p.code}
                                  onClick={() => handleNotify(p.code)}
                                >
                                  {notifying === p.code ? "..." : <Trans>Notify</Trans>}
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedPlan && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              <Trans>Signups —</Trans>{" "}
              <span className="text-muted-foreground font-normal">{selectedPlan}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <div className="text-muted-foreground p-4 text-center text-sm">
                <Trans>No signups for this plan.</Trans>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-border/50 border-b text-left">
                      <th className="px-3 py-2 font-medium">
                        <Trans>Name</Trans>
                      </th>
                      <th className="px-3 py-2 font-medium">
                        <Trans>Email</Trans>
                      </th>
                      <th className="hidden px-3 py-2 font-medium sm:table-cell">
                        <Trans>Country</Trans>
                      </th>
                      <th className="hidden px-3 py-2 font-medium sm:table-cell">
                        <Trans>Language</Trans>
                      </th>
                      <th className="px-3 py-2 font-medium">
                        <Trans>Date</Trans>
                      </th>
                      <th className="px-3 py-2 font-medium">
                        <Trans>Notified</Trans>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => (
                      <tr
                        key={e.id}
                        className="border-border/30 hover:bg-muted/30 border-b transition-colors"
                      >
                        <td className="px-3 py-2.5 font-medium">{e.name}</td>
                        <td className="text-muted-foreground px-3 py-2.5">{e.email}</td>
                        <td className="hidden px-3 py-2.5 uppercase sm:table-cell">
                          {e.countryCode || "—"}
                        </td>
                        <td className="hidden px-3 py-2.5 uppercase sm:table-cell">{e.locale}</td>
                        <td className="text-muted-foreground px-3 py-2.5 text-xs">
                          {new Date(e.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2.5">
                          {e.notifiedAt ? (
                            <Badge variant="outline" className="text-[0.625rem] text-emerald-600">
                              ✓ {new Date(e.notifiedAt).toLocaleDateString()}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
